import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useSetRecoilState } from 'recoil';
import styled, { css } from 'styled-components';
import { card } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useChatIframe from '@ubeya/shared-web/hooks/useChatIframe';
import usePortal from '@ubeya/shared-web/hooks/usePortal';
import useOnClickOutside from '@ubeya/shared-web/hooks/useOnClickOutside';
import {
  PM_CHAT_COUNTER,
  PM_EMPLOYEE_CLICKED,
  PM_CLOSE_CHAT_MOBILE,
  PM_CHAT_ARCHIVE_AMPLITUDE_TRACK
} from '@ubeya/shared/constants';
import { chatIframeRef } from '@ubeya/shared/atoms/shared';
import { isMobileOnly, isTablet } from 'react-device-detect';
import { trackEvent } from '@ubeya/shared-web/analytics/amplitude';

const Iframe = styled.iframe`
  ${({ $isMobileOnly, $isProjectChat }) =>
    $isMobileOnly
      ? css`
          width: 100%;
          height: 100%;
        `
      : css`
          width: 380px;
          height: calc(100% - ${$isProjectChat ? 0 : 60}px);
          margin-top: ${$isProjectChat ? 0 : 60}px;
          box-shadow: -10px 4px 30px 0 rgba(12, 27, 54, 0.1);
        `};
  ${({ hidden }) => hidden && 'display:none;'};
  ${card};
  border: none;
  position: fixed;
  z-index: ${({ $isBehindModal }) => ($isBehindModal ? 4 : 1000)};
`;

const useUpdateIframeUrl = (iframeRef, url) => {
  useEffect(() => {
    iframeRef.current?.contentWindow?.postMessage?.(url, '*');
  }, [iframeRef, url]);
};

const ChatBackground = ({ selectedUser, setSelectedUser, setChatCounter, accountId: currentAccountId }) => {
  const setChatIframeRef = useSetRecoilState(chatIframeRef);

  const { isOpen, handleToggle, url } = useChatIframe();

  const anchorRef = useMemo(
    () => ({ current: document.getElementById(isMobileOnly || isTablet ? 'chat-button-mobile' : 'chat-button') }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOpen, isMobileOnly, isTablet]
  );
  useOnClickOutside(anchorRef, !selectedUser ? () => handleToggle(false) : undefined);

  const messageHandlerFactory = useCallback(
    (message) => {
      const {
        data: { payload, type },
        data,
        origin
      } = message || {};

      if (origin !== process.env.CHAT_URL || !message) {
        return null;
      }

      const handler = () => {
        switch (type) {
          case PM_EMPLOYEE_CLICKED: {
            const employeeId = payload.employees.find(({ accountId }) => accountId === currentAccountId)?.employeeId;
            if (employeeId) {
              return setSelectedUser({
                id: employeeId
              });
            }
            return null;
          }
          case PM_CHAT_COUNTER:
            return setChatCounter(data);
          case PM_CLOSE_CHAT_MOBILE:
            return handleToggle(false);
          case PM_CHAT_ARCHIVE_AMPLITUDE_TRACK:
            return trackEvent(payload.eventName, payload.eventProperties);
          default:
            return null;
        }
      };

      return {
        onMessageRecieved: handler,
        data,
        type
      };
    },

    [setSelectedUser, setChatCounter, handleToggle, currentAccountId]
  );

  const handleMessage = useCallback(
    (message) => {
      const { onMessageRecieved } = messageHandlerFactory(message) || {};
      onMessageRecieved?.();
    },
    [messageHandlerFactory]
  );

  const { Portal } = usePortal({ enabled: true, portalId: 'modal' });
  const { top, left } = useMemo(() => anchorRef.current?.getBoundingClientRect?.() || {}, [anchorRef]);
  const { isRtl } = useContext(ConfigContext);

  const topLeftValuesExists = useMemo(() => top !== null && top !== undefined && left !== null && left !== undefined, [
    left,
    top
  ]);

  const style = useMemo(
    () =>
      isMobileOnly
        ? { top: 0, left: 0 }
        : topLeftValuesExists
        ? { top: 0, ...(isRtl ? { left: 0 } : { right: 0 }) }
        : undefined,
    [isRtl, topLeftValuesExists]
  );

  const iframeRef = useRef();
  useUpdateIframeUrl(iframeRef, url);

  useEffect(() => {
    setChatIframeRef(iframeRef);
  }, [iframeRef, setChatIframeRef]);

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  const isProjectChat = url?.indexOf('project') !== -1;

  return (
    <Portal>
      <Iframe
        ref={iframeRef}
        id="chat-iframe"
        src={`${process.env.CHAT_URL}?v26&accountId=${currentAccountId}`}
        hidden={!isOpen}
        style={style}
        $isMobileOnly={isMobileOnly}
        $isProjectChat={isProjectChat}
        $isBehindModal={!!selectedUser}
      />
    </Portal>
  );
};

export default ChatBackground;
